import React, { useState, useEffect, useRef, useCallback } from "react";
import { useMediaQuery } from "react-responsive";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../data/base";
import {
    Layout,
    Row,
    Col,
    Button,
    Spin,
    Modal,
    Typography,
    Checkbox,
    Form,
    Divider,
    Select,
    DatePicker,
    App
} from "antd";
import GatewaysOverviewCard from "../components/Gateways/GatewaysOverviewCard";
import DeviceAnalytics from "../components/Gateways/DeviceAnalytics";
import PagesAnalytics from "../components/Gateways/PagesAnalytics";
import EventsAnalytics from "../components/Gateways/EventsAnalytics";
import LinesGlassesVariantsAnalytics from "../components/Gateways/GlassesVariantsAnalytics";
import GeolocationMap from "../components/Gateways/GeolocationMap";
import FilterComponent, { TagItem } from "../components/Components/Filters/FilterComponent";
import SimpleCard from "../components/Components/Cards/SimpleCard";
import dayjs from "dayjs";
import { fetchGatewaysFromCollection } from "../services/api/gatewaysApi";
import { DownloadOutlined } from '@ant-design/icons';
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import ListAltIcon from '@mui/icons-material/ListAlt';
import {
    calculateDeviceAnalytics,
    calculateGatewaysOverview,
    calculateLinesGlassesVariants,
    calculatePagesAnalytics,
    calculateEventsAnalytics,
    calculateGeolocationData,
    calculateVariantsDetails
} from "../services/utils/gatewayReportCalculation";
import HeaderComponent from "../components/Components/Headers/HeaderComponent";
import { secureGetItem } from "../data/utils";

const { Option } = Select;
const { Text, Title } = Typography;

// Imposta il filtro iniziale agli ultimi sette giorni
const endDate = dayjs().endOf("day");
const startDate = dayjs().subtract(7, "day").startOf("day");
const defaultDateRange = [startDate, endDate];

function Gateways() {
    const { message: messageApi } = App.useApp();
    const isTabletOrMobile = useMediaQuery({ query: "(max-width: 768px)" });

    const MARGIN_BOTTOM = '1rem';
    const MARGIN_TOP = '2.5rem';

    const [loading, setLoading] = useState(true);
    const [data, setData] = useState([]);
    const [dateRange, setDateRange] = useState(defaultDateRange);
    const [catalogueRefsList, setCatalogueRefsList] = useState([]);
    const [selectedGateway, setSelectedGateway] = useState([]);
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [presetLabel, setPresetLabel] = useState("Last 7 days");

    // Stato locale per i filtri selezionati ma non ancora applicati
    const [selectedFilters, setSelectedFilters] = useState({
        gateways: [],
        gatewayObjects: []
    });
    const [filtersApplied, setFiltersApplied] = useState(false);

    // Aggiungiamo una cache per i dati dei gateway
    const gatewaysCache = useRef(new Map());

    // REPORT
    const [showModal, setShowModal] = useState(false);
    const [selectedOption, setSelectedOption] = useState([]);
    const [startDateReport, setStartDateReport] = useState('');
    const [endDateReport, setEndDateReport] = useState('');
    const [error, setError] = useState('');
    const [downloadLoading, setDownloadLoading] = useState(false);
    const [reportData, setReportData] = useState([]);

    // Aggiungiamo stato per memorizzare i dati delle cards dell'interfaccia utente
    const [uiTotals, setUiTotals] = useState({
        users: 0,
        sessions: 0,
        newUsers: 0,
        avgTime: 0,
        avgEngTime: 0,
        avgSU: 0
    });

    // CLEAR FILTERS BUTTON VISIBILITY
    const [clearFiltersVisible, setClearFiltersVisible] = useState(false);

    // Stato per le sezioni del report da includere
    const [reportSections, setReportSections] = useState({
        overview: false,
        deviceAnalytics: false,
        pagesAnalytics: false,
        eventsAnalytics: false,
        geolocation: false,
        variantsDetails: false
    });

    // Configurazione presets date range
    const rangePresets = [
        { label: "Today", value: [dayjs().startOf("day"), dayjs().endOf("day")] },
        { label: "Yesterday", value: [dayjs().subtract(1, "day").startOf("day"), dayjs().subtract(1, "day").endOf("day")] },
        { label: "Last Week", value: [dayjs().subtract(1, "week").startOf("week"), dayjs().subtract(1, "week").endOf("week")] },
        { label: "This Week", value: [dayjs().startOf("week"), dayjs().endOf("day")] },
        { label: "Last Month", value: [dayjs().subtract(1, "month").startOf("month"), dayjs().subtract(1, "month").endOf("month")] },
        { label: "This Month", value: [dayjs().startOf("month"), dayjs().endOf("day")] },
        { label: "Last 3 Months", value: [dayjs().subtract(3, "month").startOf("day"), dayjs().endOf("day")] },
        { label: "Last 6 Months", value: [dayjs().subtract(6, "month").startOf("day"), dayjs().endOf("day")] },
        { label: "This Year", value: [dayjs().startOf("year"), dayjs().endOf("day")] },
    ];

    // Funzione di utilità per verificare se due date sono uguali (anno, mese, giorno)
    const isSameDay = (date1, date2) => {
        if (!date1 || !date2) return false;
        return (
            date1.year() === date2.year() &&
            date1.month() === date2.month() &&
            date1.date() === date2.date()
        );
    };

    // Funzione per verificare se un intervallo di date corrisponde a un preset
    const findPresetLabel = (currentRange, presets) => {
        if (!currentRange || !currentRange[0] || !currentRange[1] || !presets) return null;

        // Verifichiamo se l'intervallo corrente corrisponde a uno dei preset
        for (const preset of presets) {
            const presetStart = preset.value[0];
            const presetEnd = preset.value[1];

            if (isSameDay(currentRange[0], presetStart) && isSameDay(currentRange[1], presetEnd)) {
                return preset.label;
            }
        }

        return null;
    };

    const fetchGateways = async (startDate, endDate, selectedGateways = []) => {
        try {
            setLoading(true);

            // Crea una chiave di cache basata sui parametri di ricerca
            const cacheKey = `${startDate || 'null'}-${endDate || 'null'}-${selectedGateways.sort().join(',')}`;

            // Verifica se i dati esistono in cache
            if (gatewaysCache.current.has(cacheKey)) {
                const cachedData = gatewaysCache.current.get(cacheKey);
                setData(cachedData.fetchedData);
                setCatalogueRefsList(cachedData.gatewayOptions);
                setLoading(false);
                return;
            }

            // 1. Recupera informazioni utente per il filtro basato sui permessi
            const userRole = secureGetItem("role");
            const userCataloguesList = secureGetItem("p_list_catalogues") || [];

            // 2. Prima recuperiamo i gateway dalla collezione Gateways
            const availableGateways = await fetchGatewaysFromCollection();

            // 3. Filtra i gateway in base al ruolo e ai cataloghi dell'utente
            let filteredGateways = availableGateways;

            if (userRole !== "Admin") {
                // Per gli utenti non-admin, mostra solo i gateway che hanno almeno un catalogo in comune
                filteredGateways = availableGateways.filter(gateway => {
                    const gatewayCatalogues = gateway.list_catalogues || [];
                    // Verifica se c'è almeno un catalogo in comune
                    return gatewayCatalogues.some(catalogueId => userCataloguesList.includes(catalogueId));
                });
            }

            // 4. Ora recuperiamo i dati di sessione
            let gatewaysQuery = collection(db, "Session_Gateway");
            if (startDate && endDate) {
                gatewaysQuery = query(
                    gatewaysQuery,
                    where("session_start_date", ">=", startDate),
                    where("session_start_date", "<=", endDate)
                );
            }

            const querySnapshot = await getDocs(gatewaysQuery);
            let fetchedData = querySnapshot.docs.map((doc) => ({
                id: doc.id,
                ...doc.data()
            }));

            // 5. Prepariamo le opzioni per la select dei gateway (solo quelli filtrati)
            const gatewayOptions = filteredGateways.map(gateway => ({
                value: gateway.id,
                label: gateway.nameGateway,
                catalogues: gateway.list_catalogues || []
            }));

            setCatalogueRefsList(gatewayOptions);

            // 6. Applica il filtro solo se `selectedGateways` non è vuoto
            if (selectedGateways.length > 0) {
                // Troviamo i cataloghi associati ai gateway selezionati
                const selectedCatalogues = gatewayOptions
                    .filter(gateway => selectedGateways.includes(gateway.value))
                    .flatMap(gateway => gateway.catalogues);

                // Filtriamo i dati delle sessioni in base ai cataloghi
                fetchedData = fetchedData.filter((item) =>
                    item.catalogue_refs_list?.some((ref) => selectedCatalogues.includes(ref))
                );
            } else {
                // Se nessun gateway è selezionato, filtra i dati per mostrare solo le sessioni
                // dei cataloghi accessibili all'utente (per utenti non-admin)
                if (userRole !== "Admin") {
                    fetchedData = fetchedData.filter((item) =>
                        item.catalogue_refs_list?.some((ref) => userCataloguesList.includes(ref))
                    );
                }
            }

            // Salviamo i dati nella cache
            gatewaysCache.current.set(cacheKey, {
                fetchedData,
                gatewayOptions
            });

            setData(fetchedData);
        } catch (error) {
            console.error("Error retrieving data:", error);
        } finally {
            setLoading(false);
        }
    };

    const fetchGatewaysForReport = async (startDate, endDate, selectedGateways = []) => {
        try {
            // 1. Recupera informazioni utente per il filtro basato sui permessi
            const userRole = secureGetItem("role");
            const userCataloguesList = secureGetItem("p_list_catalogues") || [];

            // 2. Recuperiamo i gateway dalla collezione Gateways
            const availableGateways = await fetchGatewaysFromCollection();

            // 3. Filtra i gateway in base al ruolo e ai cataloghi dell'utente
            let filteredGateways = availableGateways;

            if (userRole !== "Admin") {
                // Per gli utenti non-admin, mostra solo i gateway che hanno almeno un catalogo in comune
                filteredGateways = availableGateways.filter(gateway => {
                    const gatewayCatalogues = gateway.list_catalogues || [];
                    // Verifica se c'è almeno un catalogo in comune
                    return gatewayCatalogues.some(catalogueId => userCataloguesList.includes(catalogueId));
                });
            }

            // 4. Ora recuperiamo i dati di sessione
            let gatewaysQuery = collection(db, "Session_Gateway");
            if (startDate && endDate) {
                gatewaysQuery = query(
                    gatewaysQuery,
                    where("session_start_date", ">=", startDate),
                    where("session_start_date", "<=", endDate)
                );
            }

            const querySnapshot = await getDocs(gatewaysQuery);
            let fetchedData = querySnapshot.docs.map((doc) => ({
                id: doc.id,
                ...doc.data()
            }));

            // 5. Prepariamo le opzioni per la select dei gateway (solo quelli filtrati)
            const gatewayOptions = filteredGateways.map(gateway => ({
                value: gateway.id,
                label: gateway.nameGateway,
                catalogues: gateway.list_catalogues || []
            }));

            // 6. Applica il filtro solo se `selectedGateways` non è vuoto
            if (selectedGateways.length > 0) {
                // Troviamo i cataloghi associati ai gateway selezionati
                const selectedCatalogues = gatewayOptions
                    .filter(gateway => selectedGateways.includes(gateway.value))
                    .flatMap(gateway => gateway.catalogues);

                // Filtriamo i dati delle sessioni in base ai cataloghi
                fetchedData = fetchedData.filter((item) =>
                    item.catalogue_refs_list?.some((ref) => selectedCatalogues.includes(ref))
                );
            } else {
                // Se nessun gateway è selezionato, filtra i dati per mostrare solo le sessioni
                // dei cataloghi accessibili all'utente (per utenti non-admin)
                if (userRole !== "Admin") {
                    fetchedData = fetchedData.filter((item) =>
                        item.catalogue_refs_list?.some((ref) => userCataloguesList.includes(ref))
                    );
                }
            }

            return fetchedData;
        } catch (error) {
            console.error("Error retrieving data for the report:", error);
            messageApi.error("Error retrieving data for the report.");
            return [];
        }
    };

    const fetchFilteredDataForReport = async () => {
        setDownloadLoading(true);

        try {
            // Convert dates to required format
            const formattedStartDate = startDateReport ? dayjs(startDateReport).format("YYYY/MM/DD") : null;
            const formattedEndDate = endDateReport ? dayjs(endDateReport).format("YYYY/MM/DD") : null;

            // Usiamo la stessa logica per selezionare i gateway come in handleGatewayChange
            // Se "all" è presente, rimuovilo e mantieni gli altri
            // Se c'è solo "all", invia una lista vuota per selezionare tutti i gateway
            const cleanedSelectedOption = selectedOption.includes("all") ?
                (selectedOption.length === 1 ? [] : selectedOption.filter(option => option !== "all")) :
                selectedOption;

            const fetchedReportData = await fetchGatewaysForReport(formattedStartDate, formattedEndDate, cleanedSelectedOption);

            if (!Array.isArray(fetchedReportData)) {
                console.error("Error: report data is not an array");
                return [];
            }

            setReportData(fetchedReportData); // Aggiorniamo solo `reportData`
            return fetchedReportData;
        } catch (error) {
            console.error("Error retrieving data for the report:", error);
            messageApi.error("Error retrieving data for the report.");
            return [];
        } finally {
            setDownloadLoading(false);
        }
    };

    const handleDateChange = (dates) => {
        if (dates && dates.length === 2) {
            const [startDate, endDate] = dates;
            setDateRange(dates);
            setClearFiltersVisible(true);

            // Applica immediatamente i filtri di data (indipendentemente dagli altri filtri)
            fetchGateways(startDate.format("YYYY/MM/DD"), endDate.format("YYYY/MM/DD"), selectedGateway);

            // Aggiorniamo il label del preset
            const label = findPresetLabel(dates, rangePresets);
            setPresetLabel(label || `${startDate.format('DD/MM/YYYY')} - ${endDate.format('DD/MM/YYYY')}`);
        } else {
            // Quando viene resettato (clicked sulla X), ritorna ai default (ultimi 7 giorni)
            setDateRange(defaultDateRange);
            setPresetLabel("Last 7 days");
            setClearFiltersVisible(selectedFilters.gateways.length > 0);

            // Applica immediatamente i filtri con le date di default
            fetchGateways(startDate.format("YYYY/MM/DD"), endDate.format("YYYY/MM/DD"), selectedGateway);
        }
    };

    const handleGatewayChange = (values) => {
        // Trova gli oggetti gateway corrispondenti per i valori selezionati
        const selectedGatewayObjects = values.map(value => {
            const gateway = catalogueRefsList.find(g => g.value === value);
            return gateway || { value, label: `Gateway ${value.substring(0, 8)}...` };
        });

        // Aggiorna solo lo stato locale dei filtri selezionati
        setSelectedFilters(prev => ({
            ...prev,
            gateways: values,
            gatewayObjects: selectedGatewayObjects
        }));

        // Non applicare immediatamente i filtri
        setClearFiltersVisible(values.length > 0 || dateRange !== defaultDateRange);
    };

    const handleApplyFilters = () => {
        setIsFilterOpen(false);
        setFiltersApplied(true);

        // Applica i filtri selezionati
        setSelectedGateway(selectedFilters.gateways);
        const [startDate, endDate] = dateRange || [];
        fetchGateways(startDate?.format("YYYY/MM/DD"), endDate?.format("YYYY/MM/DD"), selectedFilters.gateways);

        // Aggiorna la visibilità del pulsante clear filters
        setClearFiltersVisible(selectedFilters.gateways.length > 0 || dateRange !== defaultDateRange);
    };

    const handleClearFilter = () => {
        // Reset solo i filtri gateway, NON le date
        setSelectedGateway([]);
        setSelectedFilters({
            gateways: [],
            gatewayObjects: []
        });
        setClearFiltersVisible(false);
        setFiltersApplied(false);

        // Applica i filtri resettati mantenendo le date correnti
        const [currentStartDate, currentEndDate] = dateRange || [];
        fetchGateways(
            currentStartDate?.format("YYYY/MM/DD"),
            currentEndDate?.format("YYYY/MM/DD"),
            [] // nessun gateway selezionato
        );
    };

    const handleClearAllFilters = () => {
        // Reset tutti i filtri incluse le date
        setDateRange(defaultDateRange);
        setSelectedGateway([]);
        setSelectedFilters({
            gateways: [],
            gatewayObjects: []
        });
        setClearFiltersVisible(false);
        setPresetLabel("Last 7 days");
        setFiltersApplied(false);
        fetchGateways(startDate.format("YYYY/MM/DD"), endDate.format("YYYY/MM/DD"));
    };

    const handleToggleFilter = () => {
        setIsFilterOpen(prev => !prev);
    };

    useEffect(() => {
        fetchGateways(startDate.format("YYYY/MM/DD"), endDate.format("YYYY/MM/DD"), []);
    }, []);

    // Sincronizza selectedFilters con selectedGateway quando cambiano dall'esterno
    useEffect(() => {
        const gatewayObjects = selectedGateway.map(value => {
            const gateway = catalogueRefsList.find(g => g.value === value);
            return gateway || { value, label: `Gateway ${value.substring(0, 8)}...` };
        });

        setSelectedFilters(prev => ({
            ...prev,
            gateways: selectedGateway,
            gatewayObjects: gatewayObjects
        }));
    }, [selectedGateway, catalogueRefsList]);

    const handleCloseModalGateway = () => {
        setShowModal(false);
    };

    const handleStartDateChange = (date) => {
        if (!date) {
            setStartDateReport(null);
            setError('');
            return;
        }

        const value = date.format("YYYY-MM-DD");

        if (endDateReport && dayjs(value).isAfter(dayjs(endDateReport))) {
            setError('Start date cannot be later than end date.');
        } else {
            setStartDateReport(value);
            setError('');
        }
    };

    const handleEndDateChange = (date) => {
        if (!date) {
            setEndDateReport(null);
            setError('');
            return;
        }

        const value = date.format("YYYY-MM-DD");

        if (startDateReport && dayjs(value).isBefore(dayjs(startDateReport))) {
            setError('End date cannot be earlier than start date.');
        } else {
            setEndDateReport(value);
            setError('');
        }
    };

    const handleReportSectionChange = (e) => {
        const { name, checked } = e.target;

        if (name === 'all') {
            // Se "all" è selezionato, seleziona tutte le sezioni
            setReportSections({
                overview: checked,
                deviceAnalytics: checked,
                pagesAnalytics: checked,
                eventsAnalytics: checked,
                geolocation: checked,
                variantsDetails: checked
            });
        } else {
            // Altrimenti, aggiorna solo la sezione specifica
            setReportSections(prev => ({
                ...prev,
                [name]: checked
            }));
        }
    };

    const isAllSectionsSelected = () => {
        return Object.values(reportSections).every(value => value === true);
    };

    const isAnySectionSelected = () => {
        return Object.values(reportSections).some(value => value === true);
    };

    const isFormValid = () => {
        return startDateReport && endDateReport && !error && isAnySectionSelected();
    };

    const handleOpenDownloadReportModal = async () => {
        setShowModal(true);
    };

    const handleOptionsChange = (e) => {
        const { value, checked } = e.target;

        setSelectedOption((prevSelectedOption) => {
            let updatedSelectedOption;

            if (checked) {
                updatedSelectedOption = [...prevSelectedOption, value];
            } else {
                updatedSelectedOption = prevSelectedOption.filter((gateway) => gateway !== value);
            }

            // Se "all" è selezionato e viene deselezionata una voce, rimuoviamo "all"
            if (prevSelectedOption.includes("all") && !checked) {
                updatedSelectedOption = updatedSelectedOption.filter((item) => item !== "all");
            }

            // Se tutte le checkbox (tranne "All") sono selezionate, selezioniamo anche "All"
            if (updatedSelectedOption.length === catalogueRefsList.length) {
                return ["all", ...updatedSelectedOption];
            }

            return updatedSelectedOption;
        });
    };

    const handleSelectAll = (e) => {
        if (e.target.checked) {
            // Selezionare "all" e tutti i gateway disponibili
            setSelectedOption(["all", ...catalogueRefsList.map((g) => g.value)]);
        } else {
            // Deselezionare tutto
            setSelectedOption([]);
        }
    };

    const handleReportDownload = async () => {
        setDownloadLoading(true);

        try {
            let filteredData = reportData;

            // Se non abbiamo già i dati del report, li recuperiamo
            if (!Array.isArray(filteredData) || filteredData.length === 0) {
                filteredData = await fetchFilteredDataForReport();
            }

            if (!Array.isArray(filteredData) || filteredData.length === 0) {
                messageApi.warning("No data available for the report.");
                return;
            }

            const workbook = XLSX.utils.book_new();

            // **Calculate data for each section**
            const overviewData = reportSections.overview ? calculateGatewaysOverview(filteredData) : null;
            const linesGlassesData = await calculateLinesGlassesVariants(filteredData);
            const deviceAnalyticsData = reportSections.deviceAnalytics ? calculateDeviceAnalytics(filteredData, "All Devices") : null;
            const pagesAnalyticsData = reportSections.pagesAnalytics ? calculatePagesAnalytics(filteredData) : null;
            const eventsAnalyticsData = reportSections.eventsAnalytics ? calculateEventsAnalytics(filteredData) : null;
            const geolocationData = reportSections.geolocation ? await calculateGeolocationData(filteredData) : null;
            const variantsDetailsData = reportSections.variantsDetails ? await calculateVariantsDetails(filteredData) : null;

            // **Function to create Excel sheets only if they have valid data**
            const createSheet = (data, sheetName) => {
                if (!data || (Array.isArray(data) && data.length === 0)) {
                    console.warn(`Sheet "${sheetName}" not created: empty data.`);
                    return;
                }

                // Assicuriamo che i valori numerici vengano interpretati correttamente da Excel
                const processedData = data.map(row => {
                    const newRow = { ...row };
                    // Convertiamo i numeri in stringhe con punto decimale 
                    // per assicurarci che Excel non li converta in base alle impostazioni locali
                    Object.keys(newRow).forEach(key => {
                        if (typeof newRow[key] === 'number' && !Number.isInteger(newRow[key])) {
                            // Manteniamo due decimali per i numeri con virgola
                            newRow[key] = newRow[key].toFixed(2);
                        }
                    });
                    return newRow;
                });

                const ws = XLSX.utils.json_to_sheet(processedData);

                // Impostiamo il formato delle celle per forzare l'uso del punto come separatore decimale
                if (sheetName === "Overview") {
                    // Otteniamo le intestazioni delle colonne
                    const headers = Object.keys(processedData[0] || {});

                    // Per ogni colonna che potrebbe contenere numeri decimali
                    ["Avg Time", "Avg Eng Time", "Avg S/U"].forEach(column => {
                        const colIndex = headers.indexOf(column);
                        if (colIndex >= 0) {
                            // Calcoliamo la lettera della colonna in Excel (A, B, C, ecc.)
                            const colLetter = String.fromCharCode(65 + colIndex);

                            // Per ogni riga di dati, impostiamo il formato numerico con due decimali
                            for (let i = 0; i < processedData.length; i++) {
                                const cellRef = `${colLetter}${i + 2}`; // +2 perché la riga 1 è l'intestazione
                                if (!ws[cellRef]) ws[cellRef] = {};
                                // Impostare il formato numerico con 2 decimali
                                ws[cellRef].z = "0.00";
                            }
                        }
                    });
                }

                XLSX.utils.book_append_sheet(workbook, ws, sheetName);
            };

            // **Add data to worksheets only for selected sections**
            if (reportSections.overview) {
                // Ottenere gli stessi valori esatti mostrati nell'interfaccia
                // Invece di calcolare le medie dalle righe, usiamo direttamente i dati grezzi

                // I dati uiTotals contengono esattamente i valori mostrati nell'interfaccia utente
                // e vengono memorizzati tramite il callback onDataCalculated dal componente GatewaysOverviewCard

                // NOTA: Abbiamo risolto i problemi delle discrepanze tra l'interfaccia utente e il report:
                // 1. Device Analytics ora conta le sessioni anziché i device_id unici, per corrispondere alla UI
                // 2. I numeri decimali vengono formattati esplicitamente per garantire la visualizzazione corretta
                //    con punto come separatore decimale, indipendentemente dalle impostazioni locali di Excel
                // 3. Per i totali dell'overview, ora utilizziamo DIRETTAMENTE i valori visualizzati nell'interfaccia utente,
                //    garantendo una perfetta corrispondenza dei valori tra interfaccia e report

                // Aggiungiamo la riga di totali all'overview
                if (Array.isArray(overviewData) && overviewData.length > 0) {
                    // Formattazione dei valori per ogni gateway (manteniamo il formato originale per i tempi)
                    const formattedOverviewData = overviewData.map(row => {
                        // Solo avgSU deve essere formattato come numero, avgTime e avgEngTime sono già stringhe
                        const avgSU = typeof row["Avg S/U"] === 'number'
                            ? parseFloat(row["Avg S/U"].toFixed(2))
                            : parseFloat(parseFloat(row["Avg S/U"]).toFixed(2));

                        return {
                            ...row,
                            "Avg S/U": avgSU
                            // Manteniamo il formato originale per Avg Time e Avg Eng Time (MM:SS)
                        };
                    });

                    // Aggiungiamo la riga di totali all'overview
                    const totalRow = {
                        Gateway: "Totals",
                        Users: uiTotals.users,
                        Sessions: uiTotals.sessions,
                        "New Users": uiTotals.newUsers,
                        "Avg Time": uiTotals.avgTime,
                        "Avg Eng Time": uiTotals.avgEngTime,
                        "Avg S/U": uiTotals.avgSU,
                    };

                    // Aggiungiamo la riga di totali all'overview
                    const overviewWithTotal = [...formattedOverviewData, totalRow];
                    createSheet(overviewWithTotal, "Overview");
                } else {
                    createSheet(overviewData, "Overview");
                }
            }

            // Includiamo sempre Lines nel report perché è necessario per i calcoli interni
            if (linesGlassesData && Array.isArray(linesGlassesData.lines) && linesGlassesData.lines.length > 0) {
                // Aggiungi riga totali per Lines
                const totalVisualisations = linesGlassesData.lines.reduce((sum, row) => sum + (row.visualisations || 0), 0);
                const totalSessions = linesGlassesData.lines.reduce((sum, row) => sum + (row.sessions || 0), 0);

                const totalRow = {
                    line: 'TOTALS',
                    visualisations: totalVisualisations,
                    sessions: totalSessions
                };

                const linesWithTotal = [...linesGlassesData.lines, totalRow];
                createSheet(linesWithTotal, "Lines");
            }

            if (reportSections.deviceAnalytics) {
                if (deviceAnalyticsData && Array.isArray(deviceAnalyticsData.summary) && deviceAnalyticsData.summary.length > 0) {
                    // Aggiungi percentuali al Device Summary
                    const totalDevices = deviceAnalyticsData.summary.find(item => item.label === "All Devices")?.total || 0;
                    const deviceSummaryWithPercentages = deviceAnalyticsData.summary.map(item => ({
                        'Device Category': item.label,
                        'Count': item.total,
                        'Percentage': totalDevices > 0 ?
                            `${((item.total / totalDevices) * 100).toFixed(1)}%` : '0.0%'
                    }));

                    // Aggiungi riga totali
                    const totalRow = {
                        'Device Category': 'TOTALS',
                        'Count': totalDevices,
                        'Percentage': '100.0%'
                    };
                    deviceSummaryWithPercentages.push(totalRow);

                    createSheet(deviceSummaryWithPercentages, "Device Summary");
                }
                if (deviceAnalyticsData && Array.isArray(deviceAnalyticsData.browserUsage) && deviceAnalyticsData.browserUsage.length > 0) {
                    // Aggiungi percentuali al Browser Usage
                    const totalBrowserSessions = deviceAnalyticsData.browserUsage.reduce((sum, item) => sum + item.count, 0);
                    const browserUsageWithPercentages = deviceAnalyticsData.browserUsage.map(item => ({
                        'Browser': item.name,
                        'Count': item.count,
                        'Percentage': totalBrowserSessions > 0 ?
                            `${((item.count / totalBrowserSessions) * 100).toFixed(1)}%` : '0.0%'
                    }));

                    // Aggiungi riga totali
                    const totalRow = {
                        'Browser': 'TOTALS',
                        'Count': totalBrowserSessions,
                        'Percentage': '100.0%'
                    };
                    browserUsageWithPercentages.push(totalRow);

                    createSheet(browserUsageWithPercentages, "Browser Usage");
                }
            }

            if (reportSections.pagesAnalytics) {
                if (Array.isArray(pagesAnalyticsData) && pagesAnalyticsData.length > 0) {
                    // Formatta le percentuali come numeri per Excel
                    const formattedPagesData = pagesAnalyticsData.map(row => ({
                        ...row,
                        viewingPercentage: typeof row.viewingPercentage === 'string' ?
                            parseFloat(row.viewingPercentage) : row.viewingPercentage
                    }));

                    // Aggiungi riga totali per Pages Analytics
                    const totalVisualisations = formattedPagesData.reduce((sum, row) => sum + (row.visualisations || 0), 0);
                    const totalViewingTime = formattedPagesData.reduce((sum, row) => {
                        // Converti il tempo da formato MM:SS a secondi per la somma
                        const timeStr = row.viewingTotalTime || "0:00";
                        const parts = timeStr.split(':');
                        const minutes = parseInt(parts[0] || 0);
                        const seconds = parseInt(parts[1] || 0);
                        return sum + (minutes * 60 + seconds);
                    }, 0);

                    // Formatta il tempo totale
                    const formatTime = (totalSeconds) => {
                        const hours = Math.floor(totalSeconds / 3600);
                        const minutes = Math.floor((totalSeconds % 3600) / 60);
                        const secs = Math.floor(totalSeconds % 60);
                        if (hours > 0) {
                            return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
                        } else {
                            return `${minutes}:${secs.toString().padStart(2, '0')}`;
                        }
                    };

                    const totalRow = {
                        page: 'TOTALS',
                        visualisations: totalVisualisations,
                        viewingPercentage: 100.0,
                        viewingTotalTime: formatTime(totalViewingTime),
                        viewingAvgTime: totalVisualisations > 0 ? formatTime(Math.floor(totalViewingTime / totalVisualisations)) : "0:00"
                    };

                    formattedPagesData.push(totalRow);
                    createSheet(formattedPagesData, "Pages Analytics");
                }
            }

            if (reportSections.eventsAnalytics) {
                if (Array.isArray(eventsAnalyticsData) && eventsAnalyticsData.length > 0) {
                    // Formatta i numeri con 2 decimali per le medie eventi per utente
                    const formattedEventsData = eventsAnalyticsData.map(row => ({
                        ...row,
                        avgEventPerUser: typeof row.avgEventPerUser === 'string' ?
                            parseFloat(row.avgEventPerUser) : row.avgEventPerUser
                    }));

                    // Aggiungi riga totali per Events Analytics
                    const totalEventCount = formattedEventsData.reduce((sum, row) => sum + (row.count || 0), 0);
                    const totalUniqueUsers = new Set();
                    eventsAnalyticsData.forEach(row => {
                        if (row.totalUsers) {
                            // Aggiungi gli utenti unici (simuliamo con un approccio semplificato)
                            for (let i = 0; i < row.totalUsers; i++) {
                                totalUniqueUsers.add(`${row.event}_user_${i}`);
                            }
                        }
                    });

                    const totalUsers = totalUniqueUsers.size;
                    const avgEventPerUser = totalUsers > 0 ? (totalEventCount / totalUsers).toFixed(2) : 0;

                    const totalRow = {
                        event: 'TOTALS',
                        count: totalEventCount,
                        totalUsers: totalUsers,
                        avgEventPerUser: parseFloat(avgEventPerUser)
                    };

                    formattedEventsData.push(totalRow);
                    createSheet(formattedEventsData, "Events Analytics");
                }
            }

            if (reportSections.geolocation && geolocationData) {
                // Creiamo un foglio semplificato per la geolocalizzazione
                if (Array.isArray(geolocationData.locations) && geolocationData.locations.length > 0) {
                    // Elaboriamo i dati di locations per estrarre Country e City
                    // e aggreghiamo i dati per città+paese
                    const cityCountryMap = new Map();

                    geolocationData.locations.forEach(item => {
                        const locationParts = item.location.split(' - ');
                        const country = locationParts[0] || 'Unknown';
                        // La città è sempre l'ultima parte se presente
                        const city = locationParts.length > 2 ?
                            locationParts[locationParts.length - 1] :
                            (locationParts.length > 1 ? locationParts[1] : 'Unknown');

                        // Creiamo una chiave unica per ogni combinazione città-paese
                        const key = `${country}|${city}`;

                        // Se la chiave esiste, aggiorniamo il conteggio, altrimenti creiamo una nuova entry
                        if (cityCountryMap.has(key)) {
                            const existingCount = cityCountryMap.get(key).Count;
                            cityCountryMap.set(key, {
                                Country: country,
                                City: city,
                                Count: existingCount + item.count
                            });
                        } else {
                            cityCountryMap.set(key, {
                                Country: country,
                                City: city,
                                Count: item.count
                            });
                        }
                    });

                    // Convertiamo la mappa in un array e ordiniamo per Count in ordine decrescente
                    const processedGeoData = Array.from(cityCountryMap.values())
                        .sort((a, b) => b.Count - a.Count);

                    // Aggiungi riga totali per Geo Locations
                    const totalGeoCount = processedGeoData.reduce((sum, item) => sum + (item.Count || 0), 0);
                    const totalRow = {
                        Country: 'TOTALS',
                        City: 'ALL LOCATIONS',
                        Count: totalGeoCount
                    };

                    processedGeoData.push(totalRow);
                    createSheet(processedGeoData, "Geo Locations");
                }
            }

            if (reportSections.variantsDetails) {
                if (Array.isArray(variantsDetailsData) && variantsDetailsData.length > 0) {
                    // Aggiungi riga totali per Variants Details
                    const totalVisualisations = variantsDetailsData.reduce((sum, row) => sum + (row.Visualizations || 0), 0);
                    const totalSessions = variantsDetailsData.reduce((sum, row) => sum + (row.Sessions || 0), 0);
                    const totalTime = variantsDetailsData.reduce((sum, row) => {
                        // Converti il tempo da formato MM:SS a secondi per la somma
                        const timeStr = row.Time || "0:00";
                        const parts = timeStr.split(':');
                        if (parts.length === 3) {
                            // Formato HH:MM:SS
                            const hours = parseInt(parts[0] || 0);
                            const minutes = parseInt(parts[1] || 0);
                            const seconds = parseInt(parts[2] || 0);
                            return sum + (hours * 3600 + minutes * 60 + seconds);
                        } else if (parts.length === 2) {
                            // Formato MM:SS
                            const minutes = parseInt(parts[0] || 0);
                            const seconds = parseInt(parts[1] || 0);
                            return sum + (minutes * 60 + seconds);
                        }
                        return sum;
                    }, 0);

                    // Formatta il tempo totale
                    const formatTime = (totalSeconds) => {
                        const hours = Math.floor(totalSeconds / 3600);
                        const minutes = Math.floor((totalSeconds % 3600) / 60);
                        const secs = Math.floor(totalSeconds % 60);
                        if (hours > 0) {
                            return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
                        } else {
                            return `${minutes}:${secs.toString().padStart(2, '0')}`;
                        }
                    };

                    const totalRow = {
                        'Variant Name': 'TOTALS',
                        'SKU': 'ALL VARIANTS',
                        'Frame Color': 'ALL COLORS',
                        'Visualizations': totalVisualisations,
                        'Time': formatTime(totalTime),
                        'Sessions': totalSessions
                    };

                    const variantsDetailsWithTotal = [...variantsDetailsData, totalRow];
                    createSheet(variantsDetailsWithTotal, "Variants Details");
                }
            }

            // **Save the file**
            const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
            const dataBlob = new Blob([excelBuffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8" });

            saveAs(dataBlob, `Report_${startDateReport}_${endDateReport}.xlsx`);

            messageApi.success("Report downloaded successfully!");
        } catch (error) {
            console.error("Error during report download:", error);
            messageApi.error("Error downloading the report.");
        } finally {
            setDownloadLoading(false);
            setShowModal(false);
        }
    };

    // Renderizza i gateway selezionati come tag
    const renderSelectedGateways = (inFilterDropdown = false) => {
        // Mostra i tag sempre quando ci sono filtri selezionati, non solo quando applicati
        if (selectedFilters.gateways.length === 0) return null;

        const containerStyle = !inFilterDropdown ? { marginTop: 'clamp(0.5rem, 2vw, 1rem)' } : {};

        return (
            <div style={containerStyle}>
                <div>
                    {selectedFilters.gatewayObjects.map(gatewayObj => (
                        <TagItem key={gatewayObj.value}>
                            {gatewayObj.label}
                        </TagItem>
                    ))}
                    <Text style={{
                        display: 'inline-block',
                        marginLeft: 'clamp(0.25rem, 0.5vw, 0.375rem)',
                        verticalAlign: 'middle'
                    }}>
                        {selectedFilters.gateways.length > 1 ?
                            `${selectedFilters.gateways.length} gateways selected` :
                            'Gateway selected'}
                    </Text>
                </div>
            </div>
        );
    };

    const getTagPlaceholder = () => {
        if (selectedFilters.gateways.length === 1 && selectedFilters.gateways[0] === 'all') {
            return 'All Gateways';
        }
        if (selectedFilters.gateways.length > 0) {
            return `${selectedFilters.gateways.length} gateways selected`;
        }
        return null;
    };

    // Creiamo le callbacks per i calcoli
    const memoizedCalculateGatewaysOverview = useCallback(calculateGatewaysOverview, []);
    const memoizedCalculateLinesGlassesVariants = useCallback(calculateLinesGlassesVariants, []);
    const memoizedCalculateDeviceAnalytics = useCallback(calculateDeviceAnalytics, []);
    const memoizedCalculatePagesAnalytics = useCallback(calculatePagesAnalytics, []);
    const memoizedCalculateEventsAnalytics = useCallback(calculateEventsAnalytics, []);
    const memoizedCalculateGeolocationData = useCallback(calculateGeolocationData, []);

    return (
        <App>
            <Layout>
                <Layout.Content id="gateways">
                    <HeaderComponent
                        title="Gateways - Analytics"
                        buttonText="Create your report"
                        buttonIcon={<ListAltIcon />}
                        onButtonClick={handleOpenDownloadReportModal}
                        isVisible={!isTabletOrMobile}
                    />
                    {/* Filtri */}
                    <SimpleCard>
                        <FilterComponent
                            // Stato e controllo del filtro
                            isFilterOpen={isFilterOpen}
                            onToggleFilter={handleToggleFilter}

                            // Props per il date picker
                            dateRange={dateRange}
                            onChangeDateRange={handleDateChange}
                            presetLabel={presetLabel}
                            rangePresets={rangePresets}

                            // Props per i filtri
                            onClearFilters={handleClearFilter}
                            onApplyFilter={handleApplyFilters}

                            // Contenuto filtri: solo gateway selection
                            catalogSection={
                                <Select
                                    mode="multiple"
                                    value={selectedFilters.gateways}
                                    onChange={handleGatewayChange}
                                    style={{ width: "100%" }}
                                    placeholder="Select Gateways"
                                    allowClear={false}
                                    showSearch={false}
                                    maxTagCount={selectedFilters.gateways.length === 1 && selectedFilters.gateways[0] === 'all' ? 1 : 0}
                                    maxTagPlaceholder={getTagPlaceholder()}
                                >
                                    {catalogueRefsList.map((gateway) => (
                                        <Option key={gateway.value} value={gateway.value}>
                                            {gateway.label}
                                        </Option>
                                    ))}
                                </Select>
                            }
                            catalogSectionText="Select Gateways"
                            productsSection={<div></div>}
                            serviceSection={<div></div>}
                            productsSectionText=""
                            serviceSectionText=""

                            // Props per i filtri selezionati
                            renderSelectedFilters={renderSelectedGateways}
                            hasSelectedFilters={selectedFilters.gateways.length > 0}
                            isFiltersApplied={filtersApplied}
                        />
                    </SimpleCard>

                    <Row gutter={[16, 16]}>
                        <Col span={24}>
                            <Typography.Title level={3} style={{ marginBottom: MARGIN_BOTTOM }}>
                                Gateway Overview
                            </Typography.Title>
                            <GatewaysOverviewCard
                                loading={loading}
                                aggregateList={data}
                                calculationFunction={memoizedCalculateGatewaysOverview}
                                onDataCalculated={(totals) => setUiTotals(totals)}
                            />
                        </Col>
                    </Row>

                    <Row gutter={[16, 16]}>
                        <Col span={24}>
                            <Typography.Title level={3} style={{ marginBottom: MARGIN_BOTTOM, marginTop: MARGIN_TOP }}>
                                Products & Variants Analytics
                            </Typography.Title>
                            <LinesGlassesVariantsAnalytics
                                data={data}
                                loading={loading}
                                clearFiltersVisible={clearFiltersVisible}
                                onResetFilters={handleClearFilter}
                                calculationFunction={memoizedCalculateLinesGlassesVariants}
                            />
                        </Col>
                    </Row>

                    <Row gutter={[16, 16]}>
                        <Col span={24}>
                            <Typography.Title level={3} style={{ marginBottom: MARGIN_BOTTOM, marginTop: MARGIN_TOP }}>
                                Device Analytics
                            </Typography.Title>
                            <DeviceAnalytics
                                data={data}
                                loading={loading}
                                clearFiltersVisible={clearFiltersVisible}
                                onResetFilters={handleClearFilter}
                                calculationFunction={memoizedCalculateDeviceAnalytics}
                            />
                        </Col>
                    </Row>

                    {isTabletOrMobile ? (
                        <>
                            <Row gutter={[16, 16]}>
                                <Col span={24}>
                                    <Typography.Title level={3} style={{ marginBottom: MARGIN_BOTTOM, marginTop: 'MARGIN_TOP' }}>
                                        Pages Analytics
                                    </Typography.Title>
                                    <PagesAnalytics
                                        data={data}
                                        loading={loading}
                                        clearFiltersVisible={clearFiltersVisible}
                                        onResetFilters={handleClearFilter}
                                        calculationFunction={memoizedCalculatePagesAnalytics}
                                    />
                                </Col>
                            </Row>
                            <Row gutter={[16, 16]}>
                                <Col span={24}>
                                    <Typography.Title level={3} style={{ marginBottom: MARGIN_BOTTOM, marginTop: MARGIN_TOP }}>
                                        Events Analytics
                                    </Typography.Title>
                                    <EventsAnalytics
                                        data={data}
                                        loading={loading}
                                        clearFiltersVisible={clearFiltersVisible}
                                        onResetFilters={handleClearFilter}
                                        calculationFunction={memoizedCalculateEventsAnalytics}
                                    />
                                </Col>
                            </Row>
                        </>
                    ) : (
                        <Row gutter={[16, 16]}>
                            <Col span={12}>
                                <Typography.Title level={3} style={{ marginBottom: MARGIN_BOTTOM, marginTop: MARGIN_TOP }}>
                                    Pages Analytics
                                </Typography.Title>
                                <PagesAnalytics
                                    data={data}
                                    loading={loading}
                                    clearFiltersVisible={clearFiltersVisible}
                                    onResetFilters={handleClearFilter}
                                    calculationFunction={memoizedCalculatePagesAnalytics}
                                />
                            </Col>
                            <Col span={12}>
                                <Typography.Title level={3} style={{ marginBottom: MARGIN_BOTTOM, marginTop: MARGIN_TOP }}>
                                    Events Analytics
                                </Typography.Title>
                                <EventsAnalytics
                                    data={data}
                                    loading={loading}
                                    clearFiltersVisible={clearFiltersVisible}
                                    onResetFilters={handleClearFilter}
                                    calculationFunction={memoizedCalculateEventsAnalytics}
                                />
                            </Col>
                        </Row>
                    )}

                    <Row gutter={[16, 16]}>
                        <Col span={24}>
                            <Typography.Title level={3} style={{ marginBottom: MARGIN_BOTTOM, marginTop: MARGIN_TOP }}>
                                Geolocation Data
                            </Typography.Title>
                            <GeolocationMap
                                data={data}
                                loading={loading}
                                clearFiltersVisible={clearFiltersVisible}
                                onResetFilters={handleClearFilter}
                                calculationFunction={memoizedCalculateGeolocationData}
                            />
                        </Col>
                    </Row>

                    <Modal
                        title={<Title level={4} style={{ margin: 0 }}>Report Download</Title>}
                        open={showModal}
                        onCancel={handleCloseModalGateway}
                        footer={null}
                        width={1000}
                        centered
                    >
                        <Row gutter={[24, 24]}>
                            <Col span={24}>
                                <Title level={5} style={{ marginBottom: 12 }}>
                                    Select Date Range
                                </Title>
                                <Row gutter={[24, 0]} justify="start">
                                    <Col md={8} sm={12}>
                                        <Form layout="vertical" style={{ marginBottom: 0 }}>
                                            <Form.Item
                                                style={{ marginBottom: 0 }}
                                            >
                                                <DatePicker
                                                    format="DD/MM/YYYY"
                                                    value={startDateReport ? dayjs(startDateReport, "YYYY-MM-DD") : null}
                                                    onChange={(date) => handleStartDateChange(date)}
                                                    style={{ width: "100%" }}
                                                    placeholder="Select Start Date"
                                                    size="middle"
                                                />
                                            </Form.Item>
                                        </Form>
                                    </Col>
                                    <Col md={8} sm={12}>
                                        <Form layout="vertical" style={{ marginBottom: 0 }}>
                                            <Form.Item
                                                style={{ marginBottom: 0 }}
                                            >
                                                <DatePicker
                                                    format="DD/MM/YYYY"
                                                    value={endDateReport ? dayjs(endDateReport, "YYYY-MM-DD") : null}
                                                    onChange={(date) => handleEndDateChange(date)}
                                                    style={{ width: "100%" }}
                                                    size="middle"
                                                    placeholder="Select End Date"
                                                />
                                            </Form.Item>
                                        </Form>
                                    </Col>
                                </Row>
                                {error && <Text type="danger" style={{ display: 'block', marginTop: 8 }}>{error}</Text>}
                            </Col>
                        </Row>

                        <Row gutter={[32, 24]} style={{ marginTop: 16 }}>
                            <Col md={12}>
                                <Title level={5} style={{ marginBottom: 16 }}>
                                    Select Gateway(s)
                                </Title>
                                <div style={{ maxHeight: '300px', overflowY: 'auto', padding: '0 4px' }}>
                                    <Checkbox.Group
                                        value={selectedOption}
                                        onChange={() => { }}
                                        style={{ display: 'flex', flexDirection: 'column' }}
                                    >
                                        <Checkbox
                                            value="all"
                                            checked={selectedOption.includes("all")}
                                            onChange={handleSelectAll}
                                            style={{ marginBottom: 8 }}
                                        >
                                            <Text strong>All Gateways</Text>
                                        </Checkbox>
                                        <Divider style={{ margin: '12px 0' }} />
                                        {catalogueRefsList.map((gateway) => (
                                            <Checkbox
                                                key={gateway.value}
                                                value={gateway.value}
                                                checked={selectedOption.includes(gateway.value)}
                                                onChange={handleOptionsChange}
                                                style={{ marginBottom: 8 }}
                                            >
                                                {gateway.label}
                                            </Checkbox>
                                        ))}
                                    </Checkbox.Group>
                                </div>
                            </Col>

                            <Col md={12}>
                                <Title level={5} style={{ marginBottom: 16 }}>
                                    Select Report Section(s)
                                </Title>
                                <div style={{ maxHeight: '300px', overflowY: 'auto', padding: '0 4px' }}>
                                    <Checkbox
                                        name="all"
                                        checked={isAllSectionsSelected()}
                                        onChange={handleReportSectionChange}
                                        style={{ marginBottom: 8 }}
                                    >
                                        <Text strong>All Sections</Text>
                                    </Checkbox>
                                    <Divider style={{ margin: '12px 0' }} />
                                    <Checkbox
                                        name="overview"
                                        checked={reportSections.overview}
                                        onChange={handleReportSectionChange}
                                        style={{ marginBottom: 12 }}
                                    >
                                        Overview
                                    </Checkbox>
                                    <Checkbox
                                        name="deviceAnalytics"
                                        checked={reportSections.deviceAnalytics}
                                        onChange={handleReportSectionChange}
                                        style={{ marginBottom: 12 }}
                                    >
                                        Device Analytics
                                    </Checkbox>
                                    <Checkbox
                                        name="pagesAnalytics"
                                        checked={reportSections.pagesAnalytics}
                                        onChange={handleReportSectionChange}
                                        style={{ marginBottom: 12 }}
                                    >
                                        Pages Analytics
                                    </Checkbox>
                                    <Checkbox
                                        name="eventsAnalytics"
                                        checked={reportSections.eventsAnalytics}
                                        onChange={handleReportSectionChange}
                                        style={{ marginBottom: 12 }}
                                    >
                                        Events Analytics
                                    </Checkbox>
                                    <Checkbox
                                        name="geolocation"
                                        checked={reportSections.geolocation}
                                        onChange={handleReportSectionChange}
                                        style={{ marginBottom: 12 }}
                                    >
                                        Geo Locations
                                    </Checkbox>
                                    <Checkbox
                                        name="variantsDetails"
                                        checked={reportSections.variantsDetails}
                                        onChange={handleReportSectionChange}
                                    >
                                        Variants Details
                                    </Checkbox>
                                </div>
                            </Col>
                        </Row>

                        <Divider style={{ margin: '24px 0' }} />

                        <Row justify="center" style={{ marginTop: 8 }}>
                            <Button
                                type="primary"
                                onClick={handleReportDownload}
                                disabled={!isFormValid() || downloadLoading}
                                icon={
                                    downloadLoading ? (
                                        <Spin size="small" />
                                    ) : (
                                        <DownloadOutlined />
                                    )
                                }
                                size="large"
                                style={{ minWidth: 200, height: 40 }}
                            >
                                {downloadLoading ? "Downloading..." : "Proceed to Download"}
                            </Button>
                        </Row>
                    </Modal>
                </Layout.Content>
            </Layout>
        </App>
    );
}

export default Gateways;